/* eslint-disable @typescript-eslint/no-explicit-any */
export const API_BASE = process.env.NEXT_PUBLIC_API_URL || '';

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  /** Override cache TTL in ms. Pass 0 to skip cache. Default: 60000 */
  cacheTtl?: number;
}

const DEFAULT_TTL = 60_000;
const SESSION_PREFIX = 'gwai_';

// In-memory cache — fast, cleared on refresh
const _mem = new Map<string, { data: unknown; ts: number }>();
const _inflight = new Map<string, Promise<{ success: boolean; data?: unknown; error?: string; details?: unknown }>>();

function readCache<T>(path: string, ttl: number): T | null {
  // 1. Check in-memory first (fastest)
  const mem = _mem.get(path);
  if (mem && Date.now() - mem.ts < ttl) return mem.data as T;

  // 2. Fallback to sessionStorage (survives intra-session navigation)
  if (typeof sessionStorage !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(SESSION_PREFIX + path);
      if (raw) {
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts < ttl) {
          _mem.set(path, { data, ts }); // warm in-memory
          return data as T;
        }
        sessionStorage.removeItem(SESSION_PREFIX + path);
      }
    } catch { /* storage unavailable or full */ }
  }
  return null;
}

function writeCache(path: string, data: unknown): void {
  const ts = Date.now();
  _mem.set(path, { data, ts });
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(SESSION_PREFIX + path, JSON.stringify({ data, ts }));
    } catch { /* storage full — in-memory still works */ }
  }
}

/** Invalidate cached GET responses (call after mutations) */
export function invalidateCache(pathPrefix?: string) {
  if (!pathPrefix) {
    _mem.clear();
    if (typeof sessionStorage !== 'undefined') {
      try {
        const keys = Object.keys(sessionStorage).filter(k => k.startsWith(SESSION_PREFIX));
        keys.forEach(k => sessionStorage.removeItem(k));
      } catch { /* ignore */ }
    }
    return;
  }
  for (const key of _mem.keys()) {
    if (key.startsWith(pathPrefix)) _mem.delete(key);
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      const keys = Object.keys(sessionStorage).filter(k => k.startsWith(SESSION_PREFIX + pathPrefix));
      keys.forEach(k => sessionStorage.removeItem(k));
    } catch { /* ignore */ }
  }
}

export async function api<T = any>(path: string, options: FetchOptions = {}): Promise<{ success: boolean; data?: T; error?: string; details?: unknown; status?: number }> {
  const { method = 'GET', body, headers = {}, cacheTtl = DEFAULT_TTL } = options;
  const isGet = method === 'GET';
  const cacheKey = path;

  if (isGet && cacheTtl > 0) {
    const cached = readCache<{ success: boolean; data?: T }>(cacheKey, cacheTtl);
    if (cached) return cached;

    const inflight = _inflight.get(cacheKey) as Promise<{ success: boolean; data?: T; error?: string; details?: unknown }> | undefined;
    if (inflight) return inflight;
  }

  const config: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json', ...headers },
    credentials: 'include',
  };

  // !== undefined (bukan truthy): PUT config nilai mentah bisa berupa "" / false / 0.
  if (body !== undefined && body !== null && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  const request = (async () => {
    try {
      const res = await fetch(`${API_BASE}${path}`, config);
      const raw = await res.text();
      let data: { success?: boolean; data?: T; error?: string; details?: unknown; status?: number };

      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {
          success: false,
          error: raw || `Server mengembalikan response tidak valid (${res.status}).`,
        };
      }

      if (!res.ok && data.success !== false) {
        data = {
          ...data,
          success: false,
          error: data.error || `Request gagal (${res.status}).`,
        };
      }
      data.status = res.status;

      if (isGet && cacheTtl > 0 && data?.success) {
        writeCache(cacheKey, data);
      } else if (!isGet && data?.success) {
        invalidateCache();
      }
      return data as { success: boolean; data?: T; error?: string; details?: unknown; status?: number };
    } catch (err) {
      console.error('[api-client] network error:', path, err);
      return { success: false, error: 'Gagal terhubung ke server. Periksa koneksi kamu.' };
    }
  })();

  if (isGet && cacheTtl > 0) {
    const tracked = request.finally(() => _inflight.delete(cacheKey));
    _inflight.set(cacheKey, tracked);
    return tracked;
  }

  return request;
}

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    api('/api/auth/login', { method: 'POST', body: { email, password } }),
  register: (data: { name: string; email: string; password: string; phone?: string }) =>
    api('/api/auth/register', { method: 'POST', body: data }),
  logout: () => api('/api/auth/logout', { method: 'POST' }),
  me: () => api('/api/auth/me'),
  forgotPassword: (email: string) =>
    api('/api/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (token: string, password: string) =>
    api('/api/auth/reset-password', { method: 'POST', body: { token, password } }),
  listSessions: () => api('/api/auth/sessions', { cacheTtl: 0 }),
  revokeSession: (id: string) => api(`/api/auth/sessions/${id}`, { method: 'DELETE' }),
  logoutAll: () => api('/api/auth/logout-all', { method: 'POST' }),
};

// Courses
export const coursesApi = {
  list: (params?: string) => api(`/api/courses${params ? `?${params}` : ''}`),
  get: (slug: string) => api(`/api/courses/${slug}`),
};

// Resources
export const resourcesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : '';
    return api(`/api/resources${qs ? `?${qs}` : ''}`);
  }
};

// Skills (taxonomy)
export const skillsApi = {
  list: () => api('/api/skills', { cacheTtl: 300_000 }),
  detail: (slug: string, format?: string) =>
    api(`/api/skills/${slug}${format ? `?format=${encodeURIComponent(format)}` : ''}`),
};

// Global search
export const searchApi = {
  query: (params: { q: string; skill?: string; type?: string }) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v)) as Record<string, string>
    ).toString();
    return api(`/api/search?${qs}`, { cacheTtl: 30_000 });
  },
};

// Learning paths
export const pathsApi = {
  list: (featuredOnly?: boolean) => api(`/api/paths${featuredOnly ? '?featured=1' : ''}`, { cacheTtl: 30_000 }),
  detail: (slug: string) => api(`/api/paths/${slug}`, { cacheTtl: 15_000 }),
};

// AI Content Automation
export const automationApi = {
  templates: () => api('/api/automations/templates', { cacheTtl: 300_000 }),
  list: () => api('/api/automations', { cacheTtl: 0 }),
  get: (id: string) => api(`/api/automations/${id}`, { cacheTtl: 0 }),
  create: (body: unknown) => api('/api/automations', { method: 'POST', body, cacheTtl: 0 }),
  update: (id: string, body: unknown) => api(`/api/automations/${id}`, { method: 'PATCH', body, cacheTtl: 0 }),
  setStatus: (id: string, status: 'active' | 'paused' | 'draft') =>
    api(`/api/automations/${id}/status`, { method: 'POST', body: { status }, cacheTtl: 0 }),
  remove: (id: string) => api(`/api/automations/${id}`, { method: 'DELETE', cacheTtl: 0 }),
  runNow: (id: string) => api(`/api/automations/${id}/run`, { method: 'POST', cacheTtl: 0 }),
  addTopics: (id: string, topics: string[]) =>
    api(`/api/automations/${id}/topics`, { method: 'POST', body: { topics }, cacheTtl: 0 }),
  deleteTopic: (id: string, topicId: string) =>
    api(`/api/automations/${id}/topics/${topicId}`, { method: 'DELETE', cacheTtl: 0 }),
  listCredentials: () => api('/api/automations/credentials', { cacheTtl: 0 }),
  createCredential: (body: { type: string; label: string; fields: Record<string, string> }) =>
    api('/api/automations/credentials', { method: 'POST', body, cacheTtl: 0 }),
  deleteCredential: (id: string) =>
    api(`/api/automations/credentials/${id}`, { method: 'DELETE', cacheTtl: 0 }),
};

// User
export const userApi = {
  dashboard: () => api('/api/user/dashboard', { cacheTtl: 30_000 }),
  myClasses: () => api('/api/user/my-classes'),
  transactions: () => api('/api/user/transactions'),
  membership: () => api('/api/user/membership'),
  progress: (courseId: string) => api(`/api/user/progress/${courseId}`),
  completeLesson: (enrollmentId: string, lessonId: string) =>
    api('/api/user/progress/complete', { method: 'POST', body: { enrollment_id: enrollmentId, lesson_id: lessonId } }),
  notifications: () => api('/api/user/notifications'),
  markNotificationRead: (id: string) =>
    api(`/api/user/notifications/${id}/read`, { method: 'PATCH', cacheTtl: 0 }),
  markAllNotificationsRead: () =>
    api('/api/user/notifications/read-all', { method: 'PATCH', cacheTtl: 0 }),
  profile: () => api('/api/user/profile'),
  recommendations: () => api('/api/resources/recommendations'),
  checklist: () => api('/api/user/checklist', { cacheTtl: 0 }),
};

/**
 * Catat kunjungan menu untuk checklist onboarding.
 * Sengaja di luar api() — wrapper itu me-wipe seluruh cache klien setiap POST sukses,
 * sedangkan ini terpicu pada navigasi rutin.
 */
export function recordMenuVisit(milestone: string): void {
  fetch(`${API_BASE}/api/user/milestones`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ milestone }),
  }).catch(() => { /* fire-and-forget */ });
}

function getAffiliateCookie(): string {
  if (typeof document === 'undefined') return '';
  return document.cookie.split(';').find(c => c.trim().startsWith('affiliate_code='))?.split('=')[1]?.trim() || '';
}

// Funnels — order bump & upsell pasca-bayar
export const funnelsApi = {
  getBump: (productType: string, productId: string) =>
    api(`/api/public/funnels/bump?product_type=${encodeURIComponent(productType)}&product_id=${encodeURIComponent(productId)}`, { cacheTtl: 0 }),
  getOffer: (orderId: string) => api(`/api/funnels/offer/${orderId}`, { cacheTtl: 0 }),
  accept: (orderId: string, stepType: string, paymentMethod = "qris") =>
    api(`/api/funnels/offer/${orderId}/accept`, { method: "POST", body: { step_type: stepType, payment_method: paymentMethod } }),
  decline: (orderId: string, stepType: string) =>
    api(`/api/funnels/offer/${orderId}/decline`, { method: "POST", body: { step_type: stepType } }),
};

// Checkout embedded (Pakasir) — pembayaran QRIS/VA di dalam app
export const checkoutApi = {
  createPayment: (data: { order_type: string; product_id: string; payment_method: string; coupon_code?: string; affiliate_code?: string; mobile?: string; variant_id?: string; custom_amount?: number; addons?: Array<{ type: string; step_id?: string }> }, idempotencyKey?: string) =>
    api('/api/checkout/create-payment', { 
      method: 'POST', 
      body: { ...data, affiliate_code: data.affiliate_code || getAffiliateCookie() || undefined },
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
    }),
  get: (orderId: string) => api(`/api/checkout/${orderId}`, { cacheTtl: 0 }),
  status: (orderId: string) => api(`/api/checkout/${orderId}/status`, { cacheTtl: 0 }),
  check: (orderId: string) => api(`/api/checkout/${orderId}/check`, { method: 'POST' }),
  repay: (orderId: string, paymentMethod: string) =>
    api(`/api/checkout/${orderId}/repay`, { method: 'POST', body: { payment_method: paymentMethod } }),
};

// Membership
export const membershipApi = {
  plans: () => api('/api/membership/plans'),
};

// Creator Store (toko)

// Affiliate
export const affiliateApi = {
  me: () => api('/api/affiliate/me'),
  register: () => api('/api/affiliate/register', { method: 'POST' }),
  commissions: () => api('/api/affiliate/commissions'),
  leaderboard: () => api('/api/affiliate/leaderboard'),
  trackClick: (data: { affiliate_code: string; landing_url?: string; referrer_url?: string; campaign?: string }) =>
    api('/api/affiliate/track-click', { method: 'POST', body: data, cacheTtl: 0 }),
};

// Webinars
export const webinarsApi = {
  list: () => api('/api/user/webinars', { cacheTtl: 30_000 }),
  detail: (slug: string) => api(`/api/user/webinars/${slug}`, { cacheTtl: 15_000 }),
  selectMethod: (id: string, method: string) =>
    api(`/api/user/webinars/${id}/select-method`, {
      method: 'POST',
      body: { method },
      cacheTtl: 0,
    }),
  confirmFreeShare: (id: string) =>
    api(`/api/user/webinars/${id}/confirm-free-share`, {
      method: 'POST',
      body: { confirmed: true },
      cacheTtl: 0,
    }),
};

export const communityApi = {
  list: (params?: string) => api(`/api/community/posts${params ? `?${params}` : ''}`, { cacheTtl: 15_000 }),
  detail: (id: string) => api(`/api/community/posts/${id}`, { cacheTtl: 15_000 }),
  comments: (id: string, params?: string) =>
    api(`/api/community/posts/${id}/comments${params ? `?${params}` : ''}`, { cacheTtl: 10_000 }),
  toggleLike: (id: string) =>
    api(`/api/community/posts/${id}/like`, { method: 'POST', cacheTtl: 0 }),
  createComment: (id: string, bodyText: string) =>
    api(`/api/community/posts/${id}/comments`, { method: 'POST', body: { body_text: bodyText }, cacheTtl: 0 }),
  deleteComment: (postId: string, commentId: string) =>
    api(`/api/community/posts/${postId}/comments/${commentId}`, { method: 'DELETE', cacheTtl: 0 }),
  create: async (formData: FormData): Promise<{ success: boolean; data?: any; error?: string; details?: unknown }> => {
    try {
      const res = await fetch(`${API_BASE}/api/community/posts`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({ success: false, error: `Request gagal (${res.status}).` }));
      if (data?.success) invalidateCache('/api/community');
      return data;
    } catch (err) {
      console.error('[api-client] community create error:', err);
      return { success: false, error: 'Gagal terhubung ke server. Periksa koneksi kamu.' };
    }
  },
};

// Panduan onboarding admin tenant — /api/admin/onboarding.
// GET menggabungkan tiga sumber (setup tenant, progress tenant, milestone
// per-admin) supaya shell admin cukup satu permintaan di seluruh rute.
export const adminOnboardingApi = {
  get: () => api('/api/admin/onboarding', { cacheTtl: 0 }),
  recordMilestone: (milestone: string) =>
    api('/api/admin/onboarding/milestones', { method: 'POST', body: { milestone }, cacheTtl: 0 }),
  patchProgress: (patch: unknown) =>
    api('/api/admin/onboarding/progress', { method: 'PATCH', body: patch, cacheTtl: 0 }),
};

// Kustomisasi website tenant (admin) — /admin/website
export const adminWebsiteApi = {
  getLanding: () => api('/api/admin/website/landing', { cacheTtl: 0 }),
  putLanding: (config: unknown) =>
    api('/api/admin/website/landing', { method: 'PUT', body: config, cacheTtl: 0 }),
  // Riwayat versi beranda — tiap baris = keadaan SEBELUM salah satu penyimpanan.
  getLandingVersions: () => api('/api/admin/website/landing/versions', { cacheTtl: 0 }),
  restoreLandingVersion: (id: string) =>
    api(`/api/admin/website/landing/versions/${id}/restore`, { method: 'POST', cacheTtl: 0 }),
  getNav: () => api('/api/admin/website/nav', { cacheTtl: 0 }),
  putNav: (overrides: unknown) =>
    api('/api/admin/website/nav', { method: 'PUT', body: overrides, cacheTtl: 0 }),
  getPage: (key: string) => api(`/api/admin/website/pages/${key}`, { cacheTtl: 0 }),
  putPage: (key: string, content: unknown) =>
    api(`/api/admin/website/pages/${key}`, { method: 'PUT', body: content, cacheTtl: 0 }),
  getBranding: () => api('/api/admin/website/branding', { cacheTtl: 0 }),
  putBranding: (update: unknown) =>
    api('/api/admin/website/branding', { method: 'PUT', body: update, cacheTtl: 0 }),
  // Config web whitelisted (certificate_config|business_profile|auth_config|email_footer_text)
  // — respons flat { key, value } (value null bila belum diset); PUT body = nilai mentah.
  getConfig: (key: string) => api(`/api/admin/website/config/${key}`, { cacheTtl: 0 }),
  putConfig: (key: string, value: unknown) =>
    api(`/api/admin/website/config/${key}`, { method: 'PUT', body: value, cacheTtl: 0 }),
  // Merge PARSIAL admin_setup_progress. Wajib dipakai alih-alih putConfig:
  // PUT mengganti seluruh objek, jadi penulis yang cuma peduli satu field ikut
  // menghapus milik penulis lain (mis. tunda menghapus wizard_done → modal
  // brand quick-start muncul lagi). Satu tenant bisa punya beberapa admin.
  patchSetupProgress: (patch: unknown) =>
    api('/api/admin/website/setup-progress', { method: 'PATCH', body: patch, cacheTtl: 0 }),
  // Resolusi etalase untuk config DRAFT (read-only, tidak menyentuh app_settings).
  // Editor menyimpan {kind, slug}; renderer butuh kartu lengkap harga/thumbnail.
  previewStore: (config: unknown) =>
    api('/api/admin/website/store/preview', { method: 'POST', body: config, cacheTtl: 0 }),
  // Fitur modul per tenant — respons { tier, ceiling, overrides, effective }.
  getFeatures: () => api('/api/admin/website/features', { cacheTtl: 0 }),
  // Cek data aktif modul sebelum dimatikan — respons { key, usage: {active,label}|null }.
  getFeatureUsage: (key: string) =>
    api(`/api/admin/website/features/${key}/usage-check`, { cacheTtl: 0 }),
  // Domain custom tenant — GET sekaligus polling status + aktivasi otomatis
  // (saat SSL active, hostname tenant otomatis flip). POST rate-limited 3/jam.
  getDomain: () => api('/api/admin/website/domain', { cacheTtl: 0 }),
  postDomain: (domain: string) =>
    api('/api/admin/website/domain', { method: 'POST', body: { domain }, cacheTtl: 0 }),
  deleteDomain: () => api('/api/admin/website/domain', { method: 'DELETE', cacheTtl: 0 }),
};

// Sender domain-sendiri (white-label email) — /admin/email/domain.
// From: email tenant memakai domain sendiri hanya setelah DNS terverifikasi.
export const adminEmailApi = {
  getSenderDomain: () => api('/api/admin/email/domain', { cacheTtl: 0 }),
  setSenderDomain: (body: { domain: string; from_local?: string; sender_name?: string }) =>
    api('/api/admin/email/domain', { method: 'POST', body, cacheTtl: 0 }),
  verifySenderDomain: () => api('/api/admin/email/domain/verify', { method: 'POST', cacheTtl: 0 }),
  deleteSenderDomain: () => api('/api/admin/email/domain', { method: 'DELETE', cacheTtl: 0 }),
};

// Billing langganan platform tenant (admin) — /admin/settings kartu Billing.
// Path dikecualikan dari status guard degrade (renew tetap bisa saat menunggak).
export const adminBillingApi = {
  get: () => api('/api/admin/billing', { cacheTtl: 0 }),
  // Satu endpoint: paket premium bulanan (months) atau enterprise tahunan (years).
  // Tenant Free/Premium kirim { months }; Reguler/VIP kirim { years }.
  renew: (body: { months?: 1 | 3 | 6 | 12; years?: 1 | 2 }, paymentMethod: string) =>
    api('/api/admin/billing/renew', {
      method: 'POST',
      body: { ...body, payment_method: paymentMethod },
      cacheTtl: 0,
    }),
  getBalance: () => api('/api/admin/billing/balance', { cacheTtl: 0 }),
  getPayouts: () => api('/api/admin/billing/payouts', { cacheTtl: 0 }),
  getPayoutAccount: () => api('/api/admin/billing/payout-account', { cacheTtl: 0 }),
  putPayoutAccount: (body: { bank_name: string; bank_account: string; account_holder: string }) =>
    api('/api/admin/billing/payout-account', { method: 'PUT', body, cacheTtl: 0 }),
};

// Program affiliate growclass (admin tenant mereferensikan tenant BARU) —
// /admin/growclass-affiliate. Beda dari adminAffiliatesApi (affiliate siswa
// milik tenant ini) — lihat AdminDashboardLayout.tsx untuk konteks lengkap.
export const adminGrowclassAffiliateApi = {
  get: () => api('/api/admin/growclass-affiliate', { cacheTtl: 0 }),
  optIn: () => api('/api/admin/growclass-affiliate/opt-in', { method: 'POST', cacheTtl: 0 }),
  getStats: () => api('/api/admin/growclass-affiliate/stats', { cacheTtl: 0 }),
  getLedger: () => api('/api/admin/growclass-affiliate/ledger', { cacheTtl: 0 }),
  getPayoutAccount: () => api('/api/admin/growclass-affiliate/payout-account', { cacheTtl: 0 }),
  putPayoutAccount: (body: { bank_name: string; bank_account: string; account_holder: string }) =>
    api('/api/admin/growclass-affiliate/payout-account', { method: 'PUT', body, cacheTtl: 0 }),
};
